import { AxiosProgressEvent } from 'axios';
// import { RESET } from 'jotai/utils';
import {
  deleteFileRequestApiCall,
  fileProcessStatusCheckApiCall,
  getSessionDetailsApiCall,
  intiateFileProcessApiCall,
  intiateFileUploadApiCall,
  uploadFileApiCall,
} from '../../../services/FileUpload';
import { fileUploadSegment } from '../stateSegments/fileUploadSegment';
import { translate } from '../../../localization';

import store from '../../markettingContentGeneration';
import {
  ANALYTICS,
  fileProcessStatuses,
  fileUploadStatuses,
} from '../../../constants/common';
import {
  trackFileExtractionComplete,
  trackFileExtractionError,
  trackFileExtractionInitiate,
  trackFileUploadComplete,
  trackFileUploadError,
  trackFileUploadInitiate,
} from '../../../services/Analytics';
import { trackfileUploadUsage } from '../../../services/Analytics/fileUpload';
import { getEncodedUserNtid } from '../../markettingContentGeneration/helpers/common';
import { trackfileExtractionUsage } from '../../../services/Analytics/fileExtraction';
import {
  getExtractionDetails,
  loadPromptFieldOptions,
} from '../../../services/Content';
import { ExtractionDetailsFuncResp } from '../../../types/ExtractionDetails';
import { extractionDetailsSegment } from '../stateSegments/extractionDetailsSegment';
import Logger from '../../../utils/logger';
import { FormFields } from '../../../types/PromptForm';
import { CannedParametersResult } from '../../../types/Content';
import { actionsSegment } from '../stateSegments/actionsSegment';

let controller = new AbortController();
let { signal } = controller;

const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
  const percentCompleted = Math.round(
    (progressEvent.loaded * 100) / (progressEvent.total || 100)
  );
  store.set(fileUploadSegment, prev => ({
    ...prev,
    fileUploadPercentage: percentCompleted,
  }));
};

/**
 * Simulates uploading a file to a private URL with a delay to mimic real-time file upload.
 * This function sets the loading state, simulates a delay, and then updates the state to
 * indicate that the file has been successfully uploaded.
 */
export const uploadFileToPrivateUrl = async (
  preSignedURL: string,
  file: File
): Promise<void> => {
  // Set the initial loading state
  store.set(fileUploadSegment, prev => ({
    ...prev,
    isLoading: true,
    fileUploading: true,
    error: null,
    success: null,
  }));

  try {
    if (import.meta.env.MODE === 'development') {
      setTimeout(() => {
        onUploadProgress({
          loaded: 50,
          total: 100,
          bytes: 0,
          lengthComputable: false,
        });
      }, 500);
    }

    const response = await uploadFileApiCall(
      preSignedURL,
      file,
      signal,
      onUploadProgress
    );

    if (response.status === 200) {
      // Update the state to indicate the file has been uploaded
      store.set(fileUploadSegment, prev => ({
        ...prev,
        isLoading: false,
        fileUploading: false,
        fileUploaded: true,
        fileUploadPercentage: 100,
        error: null,
      }));
      const { sessionId, fileName } = store.get(fileUploadSegment);

      // track analytics for file upload complete
      trackFileUploadComplete(ANALYTICS.FILE_UPLOAD_FORM_NAME);

      intiateFileProcess(
        sessionId as string,
        fileName,
        fileUploadStatuses.FILE_UPLOAD_COMPLETED
      );
    } else {
      store.set(fileUploadSegment, prev => ({
        ...prev,
        isLoading: false,
        fileUploading: false,
        fileUploaded: false,
        fileUploadError: true,
        fileUploadPercentage: 0,
        error: translate('file_upload_error_default_message'),
        success: null,
      }));

      // track analytics for file upload initiate
      trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);
    }
  } catch {
    // Update the state to indicate the file has been uploaded
    store.set(fileUploadSegment, prev => ({
      ...prev,
      isLoading: false,
      fileUploading: false,
      fileUploaded: false,
      fileUploadError: true,
      fileUploadPercentage: 0,
      error: translate('file_upload_error_default_message'),
      success: null,
    }));

    // track analytics for file upload initiate
    trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);
  }
};

export const intiateFileUpload = async (file: File) => {
  const { name: fileName } = file;
  store.set(fileUploadSegment, prev => {
    return {
      ...prev,
      isLoading: true,
      fileUploading: true,
      fileDeleted: false,
      fileName,
      error: null,
      success: null,
    };
  });

  // track analytics for file upload initiate
  trackFileUploadInitiate(ANALYTICS.FILE_UPLOAD_FORM_NAME);

  // track the ntid of the user
  trackfileUploadUsage(ANALYTICS.FILE_UPLOAD_FORM_NAME, getEncodedUserNtid());

  const { success, data } = await intiateFileUploadApiCall(fileName);

  if (success && data) {
    const { sessionId, fileUpload } = data;
    if (fileUpload.status !== fileUploadStatuses.FILE_UPLOAD_ERROR) {
      store.set(fileUploadSegment, prev => {
        return {
          ...prev,
          isLoading: false,
          fileName,
          sessionId,
        };
      });
      uploadFileToPrivateUrl(fileUpload.preSignedURL, file);
    } else {
      // track analytics for file upload error
      trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);
      store.set(fileUploadSegment, prev => {
        return {
          ...prev,
          isLoading: false,
          fileUploading: false,
          fileUploadError: true,
          error: translate('file_upload_error_default_message'),
          success: null,
          fileName,
          sessionId,
        };
      });
    }
  } else {
    // track analytics for file upload error
    trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);
    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        isLoading: false,
        fileUploading: false,
        fileUploadError: true,
        error: translate('file_upload_error_default_message'),
        success: null,
        fileName: '',
      };
    });
  }
};

const fetchActions = async () => {
  try {
    const response = await loadPromptFieldOptions(FormFields.ACTION, {});
    const { data, success } = response as CannedParametersResult;
    if (success) {
      store.set(actionsSegment, data.items);
      store.set(fileUploadSegment, prev => ({
        ...prev,
        fileAnalyzed: true,
        fileAnalyzing: true,
        fileProcessed: false,
        fileCompleted: true,
        sessionLoaded: true,
        sessionLoading: false,
      }));
    }
  } catch (error) {
    Logger.info(error);
  }
};

export const validateFileProcess = async (): Promise<boolean> => {
  try {
    const { sessionId } = store.get(fileUploadSegment);
    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        isLoading: true,
        error: null,
        success: null,
      };
    });
    const response = await fileProcessStatusCheckApiCall(sessionId);

    if (response.success && response.data) {
      const { fileProcessStatus } = response.data;
      const status = fileProcessStatus.fileProcessStatus;
      const { fileDeleted, sessionLoaded } = store.get(fileUploadSegment);
      if (!fileDeleted) {
        let fileProcessedError = false;
        if (status === fileProcessStatuses.FILE_PROCESS_COMPLETED) {
          const extractionDetails = await getExtractionDetails(sessionId);
          const { data: extractionData } =
            extractionDetails as ExtractionDetailsFuncResp;
          store.set(extractionDetailsSegment, () => extractionData);
          fileProcessedError = extractionData.fileUpload.topics.length === 0;
        }
        let updatedSegmentValue = {
          fileName: fileProcessStatus?.fileName || '',
          sessionLoaded:
            sessionLoaded ||
            status !== fileProcessStatuses.FILE_PROCESS_COMPLETED,
          sessionLoading: !sessionLoaded
            ? status === fileProcessStatuses.FILE_PROCESS_COMPLETED
            : false,
          isLoading: false,
          fileProcessing:
            status === fileProcessStatuses.FILE_PROCESS_IN_PROGRESS,
          fileProcessed: status === fileProcessStatuses.FILE_PROCESS_COMPLETED,
          fileProcessingError:
            status === fileProcessStatuses.FILE_PROCESS_ERROR ||
            status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR,
          fileUploading: status === fileUploadStatuses.FILE_UPLOAD_INITIATED,
          fileProcessingErrorStatus: status,
          fileProcessedError,
          fileUploaded:
            status === fileUploadStatuses.FILE_UPLOAD_COMPLETED ||
            status === fileProcessStatuses.FILE_PROCESS_INITIATED ||
            status === fileProcessStatuses.FILE_PROCESS_IN_PROGRESS,
          fileUploadError: status === fileUploadStatuses.FILE_UPLOAD_ERROR,
          success:
            status === fileProcessStatuses.FILE_PROCESS_COMPLETED
              ? translate('file_upload_success_message')
              : null,
          error:
            status === fileProcessStatuses.FILE_PROCESS_ERROR
              ? translate('file_upload_general_error_message')
              : status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR
                ? translate('file_upload_language_not_supported_message')
                : null,
          errorTitle:
            status === fileProcessStatuses.FILE_PROCESS_ERROR
              ? translate('file_upload_error_default_title')
              : status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR
                ? translate('file_upload_error_unsupported_content_type_title')
                : null,
          sessionId,
          fileProcessPercentage: fileProcessStatus?.fileProcessPercentage || 0,
          fileAnalyzed: false,
          fileAnalyzing: false,
          fileCompleted: false,
        };
        if (
          status === fileProcessStatuses.FILE_PROCESS_COMPLETED &&
          !fileProcessedError
        ) {
          trackFileExtractionComplete(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
          updatedSegmentValue = {
            ...updatedSegmentValue,
            fileAnalyzed: true,
            fileAnalyzing: true,
            fileProcessed: false,
            fileCompleted: true,
            sessionLoaded: true,
            sessionLoading: false,
          };
          await fetchActions();
        } else if (status === fileProcessStatuses.FILE_PROCESS_ERROR) {
          trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
          updatedSegmentValue = {
            ...updatedSegmentValue,
            sessionLoaded: true,
            sessionLoading: false,
          };
        } else {
          updatedSegmentValue = {
            ...updatedSegmentValue,
            sessionLoaded: true,
            sessionLoading: false,
          };
        }
        const {
          fileProcessed,
          fileProcessingError,
          fileProcessing,
          fileAnalyzed,
        } = updatedSegmentValue;
        const continuePolling =
          !fileProcessed &&
          !fileProcessingError &&
          !fileAnalyzed &&
          fileProcessing;
        if (continuePolling) {
          Logger.info('continue processing');
        } else {
          if (window.uploadInterval) {
            clearInterval(window.uploadInterval);
          }
          window.uploadInterval = null;
        }
        store.set(fileUploadSegment, prev => {
          return {
            ...prev,
            ...updatedSegmentValue,
          };
        });
        return continuePolling;
      }
      return false;
    }
    // Handle API error responses with specific messages
    let errorMessage = translate('file_upload_error_default_message');
    let errorTitle = translate('file_upload_error_default_title');
    const fileProcessingError = true;

    // Check if response has a message field for specific error handling
    if (!response.success && response.message) {
      const { message } = response;
      switch (message) {
        case 'TypeError':
          errorTitle = translate('file_upload_error_type_error_title');
          errorMessage = translate('file_upload_error_type_error_message');
          break;
        case 'The file is empty':
          errorTitle = translate('file_upload_error_empty_file_title');
          errorMessage = translate('file_upload_error_empty_file_message');
          break;
        case 'Unsupported content type':
          errorTitle = translate(
            'file_upload_error_unsupported_content_type_title'
          );
          errorMessage = translate(
            'file_upload_error_unsupported_content_type_message'
          );
          break;
        case 'Error Processing A Document':
        case 'Not Enough Claims':
          errorTitle = translate('file_upload_error_processing_document_title');
          errorMessage = translate(
            'file_upload_error_processing_document_message'
          );
          break;
        default:
          // For any other message, use the default error message
          errorTitle = translate('file_upload_error_default_title');
          errorMessage = translate('file_upload_error_default_message');
          break;
      }
    }

    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        // fileName: response?.data?.fileProcessStatus?.fileName || '',
        isLoading: false,
        sessionLoading: false,
        sessionLoaded: true,
        fileProcessing: false,
        fileProcessingError,
        error: errorMessage,
        errorTitle,
        success: null,
      };
    });
    if (window.uploadInterval) {
      clearInterval(window.uploadInterval);
    }
    window.uploadInterval = null;

    // track analytics for file extraction error
    trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
    return false;
  } catch {
    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        fileName: '',
        isLoading: false,
        sessionLoading: false,
        sessionLoaded: true,
        fileProcessing: false,
        fileProcessingError: false,
        error: '',
        success: null,
      };
    });
    if (window.uploadInterval) {
      clearInterval(window.uploadInterval);
    }
    window.uploadInterval = null;

    // track analytics for file extraction error
    trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
    return false;
  }
};

export const intiateFileProcess = async (
  sessionId: string,
  fileName: string,
  fileUploadStatus: string
) => {
  store.set(fileUploadSegment, prev => {
    return {
      ...prev,
      isLoading: true,
      error: null,
      success: null,
    };
  });

  // track analytics for file extraction initiate
  trackFileExtractionInitiate(ANALYTICS.FILE_EXTRACTION_FORM_NAME);

  // track the ntid for file extraction
  trackfileExtractionUsage(
    ANALYTICS.FILE_EXTRACTION_FORM_NAME,
    getEncodedUserNtid()
  );

  const { success, data } = await intiateFileProcessApiCall(
    sessionId,
    fileName,
    fileUploadStatus
  );

  if (success && data) {
    const { fileProcess } = data;

    if (
      fileProcess.fileProcessStatus !== fileProcessStatuses.FILE_PROCESS_ERROR
    ) {
      store.set(fileUploadSegment, prev => {
        return {
          ...prev,
          isLoading: false,
          fileProcessing: true,
          sessionId,
        };
      });
      const continuePolling = await validateFileProcess();
      if (continuePolling) {
        window.uploadInterval = setInterval(() => {
          validateFileProcess();
        }, 5000);
      }
    } else {
      store.set(fileUploadSegment, prev => {
        return {
          ...prev,
          isLoading: false,
          fileProcessing: false,
          fileProcessingError: true,
          fileProcessingErrorStatus: fileProcess.fileProcessStatus,
          error: translate('file_upload_error_default_message'),
        };
      });

      // track analytics for file extraction error
      trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
    }
  } else {
    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        isLoading: false,
        fileProcessing: false,
        fileProcessingError: true,
        fileProcessingErrorStatus: fileProcessStatuses.FILE_PROCESS_ERROR,
        error: translate('file_upload_error_default_message'),
      };
    });

    // track analytics for file extraction error
    trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
  }
};

export const deleteFile = async (sessionId: string, fileName: string) => {
  if (sessionId) {
    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        // isLoading: true,
        fileDeleting: true,
        error: null,
        success: null,
      };
    });
    controller.abort();
    controller = new AbortController();
    signal = controller.signal;
    if (window.uploadInterval) {
      clearInterval(window.uploadInterval);
      window.uploadInterval = null;
    }
    const { success, data } = await deleteFileRequestApiCall(
      sessionId,
      fileName
    );

    if (success && data) {
      store.set(fileUploadSegment, {
        isLoading: false,
        fileName: '',
        fileUploaded: false,
        fileUploading: false,
        fileUploadError: false,
        fileProcessing: false,
        fileProcessed: false,
        fileProcessedError: false,
        fileProcessingErrorStatus: '',
        fileProcessingError: false,
        sessionId: '',
        error: null,
        errorTitle: null,
        success: null,
        fileUploadPercentage: 0,
        fileDeleting: false,
        fileDeleted: true,
        sessionLoading: false,
        sessionLoaded: true,
        fileAnalyzing: false,
        fileAnalyzed: false,
        fileCompleted: false,
        fileProcessPercentage: 0,
        sessionFileName: '',
        // Reset session restore fields
        restore: false,
        contextSessionId: '',
        sessionContextRestored: false,
        sessionDetailsFetched: false,
      });
    } else {
      store.set(fileUploadSegment, prev => {
        return {
          ...prev,
          isLoading: false,
          fileDeleting: false,
          fileDeleted: false,
          error: translate('file_delete_error_message'),
        };
      });
    }
  } else {
    store.set(fileUploadSegment, {
      isLoading: false,
      fileName: '',
      fileUploaded: false,
      fileUploading: false,
      fileUploadError: false,
      fileProcessing: false,
      fileProcessed: false,
      fileProcessedError: false,
      fileProcessingErrorStatus: '',
      fileProcessingError: false,
      sessionId: '',
      error: null,
      errorTitle: null,
      success: null,
      fileUploadPercentage: 0,
      fileDeleting: false,
      fileDeleted: true,
      sessionLoading: false,
      sessionLoaded: true,
      fileAnalyzing: false,
      fileAnalyzed: false,
      fileCompleted: false,
      fileProcessPercentage: 0,
      sessionFileName: '',
      // Reset session restore fields
      restore: false,
      contextSessionId: '',
      sessionContextRestored: false,
      sessionDetailsFetched: false,
    });
  }
};

export const getSessionDetails = async (): Promise<{
  sessionId: string;
  restore: boolean;
  contextSessionId: string;
}> => {
  store.set(fileUploadSegment, prev => {
    return {
      ...prev,
      sessionLoading: true,
      error: null,
      success: null,
    };
  });
  const { success, data } = await getSessionDetailsApiCall();
  if (success && data) {
    const { userSessionData } = data;
    const lastSession = userSessionData.activeSessions.find(
      session => session.isLastActiveSession
    );

    const sessions = userSessionData?.activeSessions || [];

    const extractionSession = sessions.find(
      session => session.type === 'CONTENT_EXTRACTION'
    );

    const fileName = extractionSession?.fileName || '';
    let functionResponse = {
      sessionId: '',
      contextSessionId: '',
      restore: false,
    };
    if (lastSession) {
      functionResponse = {
        ...functionResponse,
        contextSessionId: lastSession.id,
        restore: true,
      };
    }
    if (extractionSession) {
      const hasSessionId = !!extractionSession.id;
      store.set(fileUploadSegment, prev => {
        return {
          ...prev,
          sessionId: extractionSession.id,
          sessionLoading: hasSessionId,
          sessionLoaded: !hasSessionId,
          sessionFileName: fileName,
          // Store session restore information
          restore: functionResponse.restore,
          contextSessionId: functionResponse.contextSessionId,
          sessionContextRestored: !functionResponse.contextSessionId,
          sessionDetailsFetched: true,
        };
      });
      functionResponse = {
        ...functionResponse,
        sessionId: extractionSession.id,
      };
      return functionResponse;
    }
    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        sessionId: '',
        sessionLoading: false,
        sessionLoaded: true,
        sessionFileName: fileName,
        // Store session restore information
        restore: functionResponse.restore,
        contextSessionId: functionResponse.contextSessionId,
        sessionContextRestored: !functionResponse.contextSessionId,
        sessionDetailsFetched: true,
      };
    });
    return functionResponse;
  }
  store.set(fileUploadSegment, prev => {
    return {
      ...prev,
      sessionId: '',
      sessionLoading: false,
      sessionLoaded: false,
      error: null,
      sessionDetailsFetched: true,
    };
  });
  return { contextSessionId: '', sessionId: '', restore: false };
};

export const markSessionContextAsRestored = () => {
  store.set(fileUploadSegment, prev => ({
    ...prev,
    sessionContextRestored: true,
  }));
};
